import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNombre } from '../lib/utils';

const DraggableProgressBar = ({ min = 0, max = 100, initialValue = 50, onChange }:any) => {
  const [value, setValue] = useState(initialValue);
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Convertit la valeur en pourcentage pour l'affichage
  const getPercentage = (val:any) => ((val - min) / (max - min)) * 100;

  const handleInteractionStart = () => {
    setIsDragging(true);
  };

  const handleInteractionMove = (e:any) => {
    if (!isDragging || !progressBarRef.current) return;
    
    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clientX = e.clientX || (e.touches?.[0]?.clientX);
    
    if (clientX === undefined) return;
    
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const newValue = Math.round(min + (x / rect.width) * (max - min));
    
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleInteractionEnd = () => {
    setIsDragging(false);
  };

  const handleBarClick = (e:any) => {
    const progressBar = progressBarRef.current;
    if (!progressBar) return;
    const rect = progressBar.getBoundingClientRect();
    const clientX = e.clientX || (e.touches?.[0]?.clientX);
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const newValue = Math.round(min + (x / rect.width) * (max - min));
    
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleInteractionMove);
    window.addEventListener('mouseup', handleInteractionEnd);
    window.addEventListener('touchmove', handleInteractionMove);
    window.addEventListener('touchend', handleInteractionEnd);

    return () => {
      window.removeEventListener('mousemove', handleInteractionMove);
      window.removeEventListener('mouseup', handleInteractionEnd);
      window.removeEventListener('touchmove', handleInteractionMove);
      window.removeEventListener('touchend', handleInteractionEnd);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-xs font-medium uppercase tracking-wider text-[#757a75]">
          {t("price")}
        </span>
        <span className="text-sm font-bold text-[#93C01F] bg-[#93C01F]/10 border border-[#93C01F]/20 rounded-lg px-2.5 py-1 whitespace-nowrap">
          {formatNombre(value)} <span className="text-[10px] font-semibold text-gray-500">FCFA</span>
        </span>
      </div>

      <div
        ref={progressBarRef}
        className="w-full h-2.5 rounded-full cursor-pointer relative bg-gray-200"
        onClick={handleBarClick}
        role="slider"
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-[#93C01F] h-full transition-all duration-300 ease-out"
          style={{ width: `${getPercentage(value)}%` }}
        />
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-5 h-5 bg-white border-2 border-[#93C01F] rounded-full cursor-ew-resize touch-none shadow-md shadow-[#93C01F]/30 hover:scale-110 transition-transform"
          style={{ left: `${getPercentage(value)}%` }}
          onMouseDown={handleInteractionStart}
          onTouchStart={handleInteractionStart}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default DraggableProgressBar;